import {
  Eye, EyeOff, Lock, Unlock, Trash2, Copy, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown,
  Type, Image, Video, Square, MousePointer, BarChart2, FileText, Mic, ZapIcon
} from 'lucide-react';
import { useEditorStore, useActivePage } from '../../store/editorStore';

function LayerIcon({ type }) {
  const size = 12;
  switch (type) {
    case 'text':        return <Type size={size} />;
    case 'image':       return <Image size={size} />;
    case 'video':       return <Video size={size} />;
    case 'shape':       return <Square size={size} />;
    case 'cta':         return <MousePointer size={size} />;
    case 'interactive': return <BarChart2 size={size} />;
    case 'form':        return <FileText size={size} />;
    case 'audio':       return <Mic size={size} />;
    case 'overlay':     return <ZapIcon size={size} />;
    default:            return <Square size={size} />;
  }
}

export default function LayersPanel() {
  const page = useActivePage();
  const selectedLayerIds = useEditorStore((s) => s.selectedLayerIds);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const deleteLayer = useEditorStore((s) => s.deleteLayer);
  const duplicateLayer = useEditorStore((s) => s.duplicateLayer);
  const toggleLayerLock = useEditorStore((s) => s.toggleLayerLock);
  const toggleLayerVisibility = useEditorStore((s) => s.toggleLayerVisibility);
  const reorderLayer = useEditorStore((s) => s.reorderLayer);

  if (!page) {
    return <div className="p-3 text-xs" style={{ color: 'var(--t-muted)' }}>No page selected</div>;
  }

  // Sort by z-index descending (top layers first)
  const sortedLayers = [...page.layers].sort(
    (a, b) => b.transform.zIndex - a.transform.zIndex
  );

  return (
    <div>
      <div className="px-3 py-2.5" style={{ borderBottom: '1px solid var(--t-border)' }}>
        <h3 className="panel-label">{page.name || 'Page'}</h3>
        <p style={{ color: 'var(--t-muted)', fontSize: '11px', marginTop: '2px', fontFamily: 'Inter, sans-serif' }}>
          {page.layers.length} {page.layers.length === 1 ? 'layer' : 'layers'}
        </p>
      </div>

      {page.layers.length === 0 ? (
        <div className="p-6 text-center">
          <p style={{ color: 'var(--t-muted)', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>No layers yet</p>
          <p style={{ color: 'var(--t-muted)', fontSize: '11px', marginTop: '4px', fontFamily: 'Inter, sans-serif', opacity: 0.7 }}>
            Add elements from the Elements panel
          </p>
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: 'var(--t-border)' }}>
          {sortedLayers.map((layer) => (
            <LayerRow
              key={layer.id}
              layer={layer}
              isSelected={selectedLayerIds.includes(layer.id)}
              onSelect={(e) => { if (!layer.locked) selectLayer(layer.id, e.metaKey || e.ctrlKey); }}
              onDelete={() => deleteLayer(layer.id)}
              onDuplicate={() => duplicateLayer(layer.id)}
              onToggleLock={() => toggleLayerLock(layer.id)}
              onToggleVisibility={() => toggleLayerVisibility(layer.id)}
              onMoveUp={() => reorderLayer(layer.id, 'forward')}
              onMoveDown={() => reorderLayer(layer.id, 'backward')}
              onMoveToFront={() => reorderLayer(layer.id, 'front')}
              onMoveToBack={() => reorderLayer(layer.id, 'back')}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LayerRow({
  layer, isSelected, onSelect, onDelete, onDuplicate,
  onToggleLock, onToggleVisibility, onMoveUp, onMoveDown, onMoveToFront, onMoveToBack,
}) {
  const type = layer.data.type;

  return (
    <div
      onClick={onSelect}
      className={`group flex items-center gap-2 px-2.5 py-2 cursor-pointer transition-colors
        ${isSelected ? 'bg-indigo-600/10 border-l-2 border-l-indigo-500' : 'border-l-2 border-l-transparent'}
        ${layer.hidden ? 'opacity-40' : ''}`}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--t-hover)'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = ''; }}
    >
      {/* Type icon */}
      <span className="flex-shrink-0" style={{ color: isSelected ? 'var(--brand-primary)' : 'var(--t-muted)' }}>
        <LayerIcon type={type} />
      </span>

      {/* Layer name */}
      <span className="flex-1 truncate min-w-0" style={{
        color: isSelected ? 'var(--t-text)' : 'var(--t-text2)',
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: isSelected ? 500 : 400,
      }}>
        {layer.name}
      </span>

      {/* Actions — fade in on row hover or when layer is selected */}
      <div className={`flex items-center gap-0.5 flex-shrink-0 transition-opacity duration-100
        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title={layer.hidden ? 'Show' : 'Hide'}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          {layer.hidden ? <EyeOff size={10} /> : <Eye size={10} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleLock(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title={layer.locked ? 'Unlock' : 'Lock'}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          {layer.locked ? <Lock size={10} /> : <Unlock size={10} />}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Duplicate"
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <Copy size={10} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveToFront(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Bring to Front"
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <ChevronsUp size={10} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Move Up"
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <ChevronUp size={10} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Move Down"
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <ChevronDown size={10} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveToBack(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Send to Back"
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--t-panel2)'; e.currentTarget.style.color = 'var(--t-text2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <ChevronsDown size={10} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="w-5 h-5 flex items-center justify-center rounded transition-colors"
          style={{ color: 'var(--t-muted)' }}
          title="Delete"
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--t-muted)'; }}
        >
          <Trash2 size={10} />
        </button>
      </div>
    </div>
  );
}
